/**
 * CMOS camera radiation detection service for FVrad
 * Analyzes dark frames for saturated pixels (radiation hits) as cosmic ray proxy.
 * Tailored for flight attendants and crew - aviation-tuned calibration.
 */

/** Saturation threshold: pixel value > this = potential radiation hit */
export const SATURATION_THRESHOLD = 250;

/** Minimum adjacent saturated pixels to count as cluster (reduces noise) */
export const CLUSTER_MIN_ADJACENT = 3;

/** Calibration: µSv/h per hit per second (aviation-tuned for cosmic rays at altitude) */
export const CALIBRATION_FACTOR_MICRO_SV_PER_HIT = 0.5;

/** Reference altitude (ft) for dose scaling - typical cruise */
const REF_ALTITUDE_FEET = 35_000;

/** Dose rate at reference altitude: 3–7 µSv/h range for 30k–40k ft */
const REF_DOSE_RATE_LOW_MICRO_SV_PER_HOUR = 3;
const REF_DOSE_RATE_HIGH_MICRO_SV_PER_HOUR = 7;

/** Temporal averaging window (frames) - reduces false positives from vibrations/cosmic noise */
export const TEMPORAL_AVERAGE_FRAMES = 30;

export interface RadiationHitResult {
  saturatedCount: number;
  clusterCount: number;
  totalHits: number;
}

export interface DoseProxyResult {
  hitsPerSecond: number;
  doseRateMicroSvPerHour: number;
  altitudeFeet: number;
}

/**
 * Analyze frame buffer for radiation hits: saturated pixels (value > threshold) or clusters.
 * Uses YUV format: Y plane (luma) first width*height bytes. Saturated = Y > 250.
 *
 * @param data - Uint8Array from frame (Y plane for YUV, or RGB interleaved)
 * @param width - Frame width
 * @param height - Frame height
 * @param pixelFormat - 'yuv' | 'rgb'
 * @param bytesPerRow - Stride per row (may differ from width for alignment)
 */
export function analyzeFrameForHits(
  data: Uint8Array,
  width: number,
  height: number,
  pixelFormat: 'yuv' | 'rgb',
  bytesPerRow: number
): RadiationHitResult {
  const step = 4; // Sample every 4th pixel for performance
  const w = Math.floor(width / step);
  const h = Math.floor(height / step);

  let saturatedCount = 0;
  const saturatedGrid: boolean[][] = [];

  if (pixelFormat === 'yuv') {
    // Y plane: first width*height bytes, Y only (luma = brightness)
    for (let sy = 0; sy < h; sy++) {
      saturatedGrid[sy] = [];
      for (let sx = 0; sx < w; sx++) {
        const y = sy * step;
        const x = sx * step;
        const idx = y * bytesPerRow + x;
        const val = data[idx] ?? 0;
        const sat = val > SATURATION_THRESHOLD;
        saturatedGrid[sy][sx] = sat;
        if (sat) saturatedCount++;
      }
    }
  } else {
    // RGB: 3 bytes per pixel, check max(R,G,B) > threshold
    const bytesPerPixel = 3;
    for (let sy = 0; sy < h; sy++) {
      saturatedGrid[sy] = [];
      for (let sx = 0; sx < w; sx++) {
        const y = sy * step;
        const x = sx * step;
        const idx = (y * bytesPerRow + x * bytesPerPixel);
        const r = data[idx] ?? 0;
        const g = data[idx + 1] ?? 0;
        const b = data[idx + 2] ?? 0;
        const maxVal = Math.max(r, g, b);
        const sat = maxVal > SATURATION_THRESHOLD;
        saturatedGrid[sy][sx] = sat;
        if (sat) saturatedCount++;
      }
    }
  }

  // Count clusters: >3 adjacent saturated pixels
  let clusterCount = 0;
  const visited = saturatedGrid.map((row) => row.map(() => false));

  for (let sy = 0; sy < h; sy++) {
    for (let sx = 0; sx < w; sx++) {
      if (!saturatedGrid[sy]?.[sx] || visited[sy][sx]) continue;
      const size = floodCount(saturatedGrid, visited, sx, sy, w, h);
      if (size >= CLUSTER_MIN_ADJACENT) clusterCount++;
    }
  }

  // Total hits: saturated pixels + clusters (clusters weighted as single events to avoid double-count)
  const totalHits = saturatedCount;

  return { saturatedCount, clusterCount, totalHits };
}

function floodCount(
  grid: boolean[][],
  visited: boolean[][],
  x: number,
  y: number,
  w: number,
  h: number
): number {
  if (x < 0 || x >= w || y < 0 || y >= h || !grid[y]?.[x] || visited[y][x]) return 0;
  visited[y][x] = true;
  let count = 1;
  count += floodCount(grid, visited, x - 1, y, w, h);
  count += floodCount(grid, visited, x + 1, y, w, h);
  count += floodCount(grid, visited, x, y - 1, w, h);
  count += floodCount(grid, visited, x, y + 1, w, h);
  return count;
}

/**
 * Altitude scaling for crew-specific dose: 3–7 µSv/h at 30k ft, higher at 40k ft.
 * Cosmic ray flux increases with altitude (less atmosphere shielding).
 */
export function getAltitudeDoseScale(altitudeFeet: number): number {
  if (altitudeFeet <= 0) return 0;
  // Power-law scaling: dose ~ altitude^1.2 (from radiationCalc)
  return Math.pow(altitudeFeet / REF_ALTITUDE_FEET, 1.2);
}

/**
 * Convert hits per second to dose rate (µSv/h) with altitude scaling.
 * Calibration tuned for cosmic rays at aviation altitudes.
 * @param calibrationFactor - Override default (µSv/h per hit/sec); used for user calibration.
 */
export function hitsToDoseRateMicroSvPerHour(
  hitsPerSecond: number,
  altitudeFeet: number = REF_ALTITUDE_FEET,
  calibrationFactor: number = CALIBRATION_FACTOR_MICRO_SV_PER_HIT
): number {
  const baseDose = hitsPerSecond * calibrationFactor;
  const scale = getAltitudeDoseScale(altitudeFeet);
  return Math.round(baseDose * scale * 100) / 100;
}

/**
 * Compute calibration factor from known cosmic dose (user feedback).
 * factor = knownDoseMicroSvPerHour / (hitsPerSecond * altitudeScale)
 */
export function computeCalibrationFromKnownDose(
  knownDoseMicroSvPerHour: number,
  observedHitsPerSecond: number,
  altitudeFeet: number = REF_ALTITUDE_FEET
): number {
  if (observedHitsPerSecond <= 0) return CALIBRATION_FACTOR_MICRO_SV_PER_HIT;
  const scale = getAltitudeDoseScale(altitudeFeet);
  if (scale <= 0) return CALIBRATION_FACTOR_MICRO_SV_PER_HIT;
  const factor = knownDoseMicroSvPerHour / (observedHitsPerSecond * scale);
  return Math.round(Math.max(0.01, Math.min(5, factor)) * 100) / 100;
}

/**
 * Temporal averaging: smooth hit counts over N frames to reduce false positives.
 */
export class TemporalHitAverager {
  private buffer: number[] = [];
  private timestamps: number[] = [];

  constructor(private readonly windowFrames: number = TEMPORAL_AVERAGE_FRAMES) {}

  add(hitCount: number, timestampMs: number): void {
    this.buffer.push(hitCount);
    this.timestamps.push(timestampMs);
    if (this.buffer.length > this.windowFrames) {
      this.buffer.shift();
      this.timestamps.shift();
    }
  }

  getHitsPerSecond(): number {
    if (this.buffer.length < 2) return 0;
    const totalHits = this.buffer.reduce((a, b) => a + b, 0);
    const durationSec = (this.timestamps[this.timestamps.length - 1]! - this.timestamps[0]!) / 1000;
    if (durationSec <= 0) return 0;
    return totalHits / durationSec;
  }

  reset(): void {
    this.buffer = [];
    this.timestamps = [];
  }
}
